import { notifyLogout } from "@utils/shared/session-storage-sync";

/**
 * No Access handling.
 *
 * The sign-in endpoints (/auth/access, /auth/ad/token, /auth/ad/access) and
 * /auth/refresh answer 403 + a ProblemDetails body for a user Oxygen KNOWS but
 * who has no access. The problem code is in the body's `title`:
 *   - "User.NoAccess"          → no access at all (Role.NoAccess)
 *   - "User.PendingApproval"   → self-registration not yet approved
 * Any other / missing title on a 403 is treated as NoAccess. A 401 is NOT this
 * case — it stays "invalid credentials".
 *
 * The No Access page reads the reason from its URL (`?reason=`), so it needs no
 * session and no API call of its own.
 */

export const NO_ACCESS_PATH = "/auth/no-access";

export const NO_ACCESS_REASON = {
  NO_ACCESS: "no-access",
  PENDING_APPROVAL: "pending-approval",
};

const REASON_BY_PROBLEM_CODE = {
  "User.NoAccess": NO_ACCESS_REASON.NO_ACCESS,
  "User.PendingApproval": NO_ACCESS_REASON.PENDING_APPROVAL,
};

/** Map a ProblemDetails body (object or raw JSON string) to a reason. */
export const noAccessReasonFromProblem = (body) => {
  let problem = body;
  if (typeof body === "string") {
    try {
      problem = JSON.parse(body);
    } catch {
      problem = null;
    }
  }
  return REASON_BY_PROBLEM_CODE[problem?.title] ?? NO_ACCESS_REASON.NO_ACCESS;
};

/** The reason for an httpClient error, or null when it isn't a 403. */
export const getNoAccessReason = (error) =>
  error?.status === 403
    ? noAccessReasonFromProblem(error.response?.data)
    : null;

/** Normalise a `?reason=` value; anything unknown shows the NoAccess text. */
export const parseNoAccessReason = (value) =>
  Object.values(NO_ACCESS_REASON).includes(value)
    ? value
    : NO_ACCESS_REASON.NO_ACCESS;

export const noAccessPath = (reason) =>
  `${NO_ACCESS_PATH}?reason=${encodeURIComponent(parseNoAccessReason(reason))}`;

/**
 * For the token-refresh paths (raw fetch, outside React): if `response` is a
 * 403, end the session everywhere and hard-redirect to the No Access page — the
 * same teardown as logout, so a user whose access is withdrawn mid-session sees
 * why instead of being bounced to the login form. Returns true when handled.
 */
export const handleRefreshForbidden = async (response) => {
  if (response?.status !== 403) return false;

  let body = null;
  try {
    body = await response.json();
  } catch {
    // Empty / non-JSON body — falls back to NoAccess.
  }

  notifyLogout();
  sessionStorage.clear();
  window.location.replace(noAccessPath(noAccessReasonFromProblem(body)));
  return true;
};
